import { useAuth } from "auth/Auth";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Grid,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { Edit, Delete } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import {
  TravelDetailSection,
  TravelDetailFieldGrid,
  formatPaymentType,
  buildTravelAuditFields,
} from "components/travel/TravelDetailSection";
import InlineEditField from "components/CustonEditableInlineField/CustomEditableInlineField";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import PaymentTemplateModal from "layouts/Travel/payment-templates/PaymentTemplateModal";

const PaymentTemplateDetail = () => {
  const { id } = useParams();
  const { axiosInstance } = useAuth();
  const location = useLocation();
  const nameFromState = location.state?.name;
  const navigate = useNavigate();

  const [trips, setTrips] = useState([]);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tripsLoaded, setTripsLoaded] = useState(false);
  const [isTripsLoading, setIsTripsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  useEffect(() => {
    if (activeTab === 1 && !tripsLoaded) {
      fetchTrips();
      setTripsLoaded(true);
    }
  }, [activeTab, tripsLoaded]);

  const fetchTemplate = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/payment-templates/${id}`);
      setTemplate(response.data);
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrips = async () => {
    setIsTripsLoading(true);
    try {
      const response = await axiosInstance.get(`/trips/?payment_template_id=${id}`);
      setTrips(response.data);
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      setIsTripsLoading(false);
    }
  };

  const handleSaveTemplate = async (formData) => {
    let payload = {
      template_name: formData.template_name,
      payment_type: formData.payment_type,
      status: formData.status,
    };
    if (formData.payment_type === "deposit_final_balance") {
      payload = {
        ...payload,
        deposit_percentage: formData.deposit_percentage,
        due_days_before_departure: formData.due_days_before_departure,
      };
    } else if (formData.payment_type === "deposit_installments") {
      payload = {
        ...payload,
        deposit_percentage: formData.deposit_percentage,
        number_of_installments: formData.number_of_installments,
        installments: formData.installments,
      };
    }
    try {
      await axiosInstance.put(`/payment-templates/${id}`, payload);
      setSnackbar({ open: true, message: "Payment Template updated successfully", severity: "success" });
      setIsModalOpen(false);
      fetchTemplate();
    } catch (error) {
      console.error("Error updating template:", error);
      setSnackbar({ open: true, message: "Error updating template", severity: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/payment-templates/${id}`);
      setSnackbar({ open: true, message: "Payment Template deleted successfully", severity: "success" });
      setTimeout(() => navigate("/payment-templates"), 1000);
    } catch (error) {
      console.error("Error deleting template:", error);
      setSnackbar({ open: true, message: "Error deleting template", severity: "error" });
      throw error;
    }
  };

  const tripsTableData = useMemo(
    () => ({
      columns: [
        {
          Header: "Trip Name",
          accessor: "title",
          width: "35%",
          Cell: ({ row }) => (
            <MDTypography
              component="span"
              onClick={() => navigate(`/trips/${row.original.id}/edit`)}
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
            >
              {row.original.title || row.original.name || "-"}
            </MDTypography>
          ),
        },
        {
          Header: "Country",
          accessor: "primary_country",
          width: "20%",
          Cell: ({ value }) => (value ? value.toUpperCase() : "-"),
        },
        {
          Header: "Created",
          accessor: "created_at",
          width: "20%",
          align: "right",
          Cell: ({ value }) => (
            <MonoText align="right">{value ? new Date(value).toLocaleDateString() : "-"}</MonoText>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          width: "15%",
          Cell: ({ value }) => <StatusBadge status={value || "draft"} />,
        },
      ],
      rows: trips,
    }),
    [trips, navigate]
  );

  const overviewFields = [
    { label: "Template Name", field: "template_name" },
    {
      label: "Payment Type",
      field: "payment_type",
      renderValue: (val) => formatPaymentType(val),
    },
    {
      label: "Deposit %",
      field: "deposit_percentage",
      renderValue: (val) => (val ? `${val}%` : "-"),
    },
    {
      label: "Status",
      field: "status",
      custom: (data) => <StatusBadge status={data?.status} />,
    },
  ];

  const auditFields = useMemo(
    () => buildTravelAuditFields((userId) => navigate(`/users/${userId}/profile`)),
    [navigate]
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h5" color="white">
                    {template?.template_name || nameFromState || "Payment Template"}
                  </MDTypography>
                  <MDTypography variant="button" color="white" opacity={0.8}>
                    Payment template details
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <Tooltip title="Edit" arrow>
                    <IconButton onClick={() => setIsModalOpen(true)} sx={commonIconButtonStyles}>
                      <Edit sx={commonIconStyles} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton onClick={() => setDeleteConfirmDialog(true)} sx={commonIconButtonStyles}>
                      <Delete sx={commonIconStyles} />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
                >
                  <Tab label="Overview" />
                  <Tab label="Trips" />
                </Tabs>

                {activeTab === 0 && (
                  <>
                    <TravelDetailSection title="Template Information">
                      <TravelDetailFieldGrid data={template} fields={overviewFields} />
                    </TravelDetailSection>

                    {template?.payment_type === "deposit_final_balance" && (
                      <TravelDetailSection title="Deposit & Final Balance">
                        <TravelDetailFieldGrid
                          data={template}
                          fields={[
                            {
                              label: "Remaining Balance %",
                              field: "deposit_percentage",
                              renderValue: (val) =>
                                val ? `${100 - Number(val)}%` : "-",
                            },
                            {
                              label: "Final Due (Days Before Departure)",
                              field: "due_days_before_departure",
                            },
                          ]}
                        />
                      </TravelDetailSection>
                    )}

                    {template?.payment_type === "deposit_installments" && (
                      <TravelDetailSection title="Installment Schedule">
                        <TravelDetailFieldGrid
                          data={template}
                          fields={[
                            {
                              label: "Max Installments",
                              field: "number_of_installments",
                            },
                          ]}
                        />
                        {template?.installments?.map((installment, index) => (
                          <MDBox
                            key={index}
                            mt={2}
                            p={2}
                            borderRadius="lg"
                            sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "divider" }}
                          >
                            <MDTypography variant="button" fontWeight="medium" mb={1}>
                              Installment {index + 1}
                            </MDTypography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <InlineEditField
                                  label="Percentage"
                                  value={installment.installment_percentage}
                                  readOnly
                                  isEditing={false}
                                  renderValue={(val) => (val ? `${val}%` : "-")}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <InlineEditField
                                  label="Days Before Departure"
                                  value={installment.days_before_departure}
                                  readOnly
                                  isEditing={false}
                                />
                              </Grid>
                            </Grid>
                          </MDBox>
                        ))}
                      </TravelDetailSection>
                    )}

                    <TravelDetailSection title="Audit Information">
                      <TravelDetailFieldGrid data={template} fields={auditFields} />
                    </TravelDetailSection>
                  </>
                )}

                {activeTab === 1 && (
                  <TravelDetailSection title="Associated Trips">
                    {isTripsLoading ? (
                      <Skeleton variant="rectangular" height={240} />
                    ) : (
                      <DataTable
                        table={tripsTableData}
                        isSorted
                        noEndBorder
                        canSearch
                        exportCSV
                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                        showTotalEntries
                        pagination={{ variant: "gradient", color: "info" }}
                      />
                    )}
                  </TravelDetailSection>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <PaymentTemplateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTemplate}
        template={template}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDeleteModal
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
        onConfirm={handleDelete}
        title="Delete Payment Template"
        message="Are you sure you want to delete this payment template? This action cannot be undone."
      />
    </DashboardLayout>
  );
};

export default PaymentTemplateDetail;
